import { opensInApp, HoverCard, HoverCardTrigger, HoverCardContent, RichTooltip, MiddleEllipsis } from './chunk-U3PKU5BY.js';
import { CopyButton } from './chunk-5KVADZ7T.js';
import { Popover, PopoverTrigger, PopoverContent } from './chunk-BLADNTOL.js';
import { Badge, badgeVariants } from './chunk-S775MXMS.js';
import { cn } from './chunk-FFX3LZW7.js';
import * as React2 from 'react';
import { useState, useEffect } from 'react';
import { getChainMetadata, resolveChain, normalizeAddress, getExplorerUrl } from '@authe/fe-commons/chains';
import { resolveChainLogoUrl } from '@authe/fe-commons/assets';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { createLogger } from '@authe/fe-commons/logging';
import { isPrivateAssetCdnKey, prepareSignedCdnImage, cdnBaseUrl, signedCdnUrl } from '@authe/fe-commons/storage';
import { RiskLevel, CatalogLifecycle, Severity } from '@authe/fe-commons/precedence';
import { Tag, Boxes, ArrowUpRight, ExternalLink, Search, Star } from 'lucide-react';
import { NATIVE_TOKEN } from '@authe/fe-commons/flows';
import { focusAddress } from '@authe/fe-commons/focus';
import { severityLabel } from '@authe/fe-commons/addresses';
import { ConfidenceTier, readConfidencePolicy, DEFAULT_TIER_CUTOFFS, tierFor, coerceConfidence, clampConfidence, formatConfidence, meetsReadConfidence } from '@authe/fe-commons/evidence';
import { BUNDLE_KEY, scoredConfidence } from '@authe/fe-commons/attribution';
import { resolveAttributionChipPrefs } from '@authe/fe-commons/display';

var SIZES = {
  sm: { box: "h-4 w-4 text-[10px]", text: "text-xs" },
  md: { box: "h-6 w-6 text-xs", text: "text-sm" },
  lg: { box: "h-8 w-8 text-sm", text: "text-base" }
};
function ChainAvatar({
  chain,
  size = "md",
  showName = true,
  note,
  logoUrl,
  logoResolver,
  className
}) {
  const resolved = getChainMetadata(chain);
  const name = resolved?.name ?? chain;
  const described = note ? `${name} \u2014 ${note}` : name;
  const initials = (resolved?.nativeToken ?? chain.slice(0, 3)).slice(0, 3).toUpperCase();
  const sz = SIZES[size];
  const candidate = logoUrl ?? logoResolver?.(chain, resolved?.slug) ?? resolved?.logoUrl ?? resolveChainLogoUrl(chain, resolved?.slug) ?? void 0;
  const brand = resolved?.brandColor ?? void 0;
  const [failedSrc, setFailedSrc] = React2.useState(null);
  const logo = candidate && candidate !== failedSrc ? candidate : void 0;
  return /* @__PURE__ */ jsxs("span", { className: cn("inline-flex items-center gap-2", className), title: described, children: [
    logo ? /* @__PURE__ */ jsx(
      "img",
      {
        src: logo,
        alt: "",
        "aria-hidden": true,
        className: cn("rounded-full object-cover", sz.box),
        onError: () => setFailedSrc(candidate ?? null)
      }
    ) : /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": true,
        className: cn(
          "inline-flex items-center justify-center rounded-full font-semibold",
          !brand && "bg-muted text-muted-foreground",
          sz.box
        ),
        style: brand ? { backgroundColor: `${brand}22`, color: brand } : void 0,
        children: initials
      }
    ),
    showName === true && /* @__PURE__ */ jsxs("span", { className: cn("font-medium", sz.text), children: [
      name,
      note && /* @__PURE__ */ jsxs("span", { className: "sr-only", children: [
        " \u2014 ",
        note
      ] })
    ] }),
    showName === "sr-only" && /* @__PURE__ */ jsx("span", { className: "sr-only", children: described })
  ] });
}
var log = createLogger("ui-core:cdn-image");
function isResolvedUrl(value) {
  return /^[a-z][a-z0-9+.-]*:/i.test(value) || value.startsWith("//");
}
function keyFromLogoUrl(logoUrl) {
  if (logoUrl.startsWith("data:") || logoUrl.startsWith("blob:")) return null;
  if (!isResolvedUrl(logoUrl)) return logoUrl.replace(/^\/+/, "") || null;
  try {
    const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const path = new URL(logoUrl, origin).pathname.replace(/^\/+/, "");
    return path || null;
  } catch {
    return null;
  }
}
function useSignedCdnImage(logoUrl) {
  const key = logoUrl ? keyFromLogoUrl(logoUrl) : null;
  const isPrivate = key != null && isPrivateAssetCdnKey(key);
  const publicSrc = logoUrl && !isPrivate && !isResolvedUrl(logoUrl) && key ? publicCdnSrc(key) : logoUrl;
  const [priv, setPriv] = useState(null);
  useEffect(() => {
    if (!isPrivate || !key) {
      setPriv(null);
      return;
    }
    let cancelled = false;
    setPriv(null);
    void prepareSignedCdnImage(key).then((r) => {
      if (!cancelled) setPriv(r);
    }).catch((error) => {
      if (!cancelled) log.warn("signed CDN image mint failed", { error: String(error) });
    });
    return () => {
      cancelled = true;
    };
  }, [isPrivate, key]);
  if (!logoUrl) return { denied: false };
  if (!isPrivate) return { src: publicSrc, denied: false };
  if (!priv) return { denied: false };
  return { src: priv.src, crossOrigin: priv.crossOrigin, denied: priv.denied };
}
function publicCdnSrc(key) {
  const base = cdnBaseUrl();
  return base ? signedCdnUrl(key, base) : void 0;
}
var SIZE = {
  sm: "h-5 w-5",
  md: "h-7 w-7"
};
function AddressAvatar({
  logoUrl,
  color,
  name,
  size = "md",
  className
}) {
  const img = useSignedCdnImage(logoUrl);
  if (!logoUrl && !color) return null;
  const dim = SIZE[size];
  const title = name || void 0;
  if (logoUrl && img.src) {
    return /* @__PURE__ */ jsx(
      "img",
      {
        src: img.src,
        ...img.crossOrigin ? { crossOrigin: img.crossOrigin } : {},
        alt: "",
        title,
        className: cn("shrink-0 rounded-md border border-border object-cover", dim, className)
      }
    );
  }
  if (!color) return null;
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn("shrink-0 rounded-md border border-black/10", dim, className),
      style: { backgroundColor: color },
      title,
      "aria-hidden": true
    }
  );
}
var STYLES = {
  [RiskLevel.SAFE]: "bg-risk-none/15 text-risk-none border-risk-none/30",
  [RiskLevel.LOW]: "bg-risk-low/15 text-risk-low border-risk-low/30",
  [RiskLevel.MEDIUM]: "bg-risk-medium/15 text-risk-medium border-risk-medium/30",
  [RiskLevel.HIGH]: "bg-risk-high/15 text-risk-high border-risk-high/30",
  [RiskLevel.SEVERE]: "bg-risk-critical/15 text-risk-critical border-risk-critical/30"
};
var LABELS = {
  [RiskLevel.SAFE]: "Safe",
  [RiskLevel.LOW]: "Low",
  [RiskLevel.MEDIUM]: "Medium",
  [RiskLevel.HIGH]: "High",
  [RiskLevel.SEVERE]: "Severe"
};
function RiskBadge({ level, score, className }) {
  return /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: cn("border", STYLES[level], className), children: [
    /* @__PURE__ */ jsx("span", { children: LABELS[level] }),
    typeof score === "number" && // Weight, not opacity. `opacity-70` over the chip fill measured
    // 2.9-3.4:1 in BOTH themes — below the bar even after the `--risk-*`
    // ramp was retuned, because it fades the text towards the very
    // background it has to stand out from. The number reads as secondary
    // because it is a numeral in a lighter weight beside a semibold word.
    /* @__PURE__ */ jsx("span", { className: "ml-1 font-normal tabular-nums", children: score.toFixed(0) })
  ] });
}
var AddressDisplayContext = React2.createContext({});
function AddressDisplayProvider({ children, ...defaults }) {
  const parent = React2.useContext(AddressDisplayContext);
  const value = React2.useMemo(() => ({
    resolveOverlay: defaults.resolveOverlay ?? parent.resolveOverlay,
    getInternalHref: defaults.getInternalHref ?? parent.getInternalHref,
    onDrilldown: defaults.onDrilldown ?? parent.onDrilldown,
    onEditCatalog: defaults.onEditCatalog === void 0 ? parent.onEditCatalog : defaults.onEditCatalog,
    presentation: defaults.presentation ?? parent.presentation,
    size: defaults.size ?? parent.size,
    showAvatar: defaults.showAvatar ?? parent.showAvatar,
    showRiskBadge: defaults.showRiskBadge ?? parent.showRiskBadge,
    showExplorerIcon: defaults.showExplorerIcon ?? parent.showExplorerIcon,
    // A nested resolver owns its own batch (e.g. the contract explorer). It
    // must not also enqueue the same values in the outer fallback batch.
    registerAddress: defaults.registerAddress ?? (defaults.resolveOverlay ? void 0 : parent.registerAddress)
  }), [parent, defaults.onEditCatalog, defaults.resolveOverlay, defaults.getInternalHref, defaults.onDrilldown, defaults.presentation, defaults.size, defaults.showAvatar, defaults.showRiskBadge, defaults.showExplorerIcon, defaults.registerAddress]);
  return /* @__PURE__ */ jsx(AddressDisplayContext.Provider, { value, children });
}
function overlayTooltipRows(overlay) {
  const tags = overlay?.tags?.filter(Boolean) ?? [];
  if (tags.length === 0) return [];
  return [{ label: tags.length === 1 ? "Tag" : "Tags", value: tags.join(", ") }];
}
function AddressDisplay(props) {
  if (props.address === NATIVE_TOKEN) {
    const symbol = (props.chain ? resolveChain(props.chain)?.nativeToken : void 0) ?? props.label ?? "Native asset";
    return /* @__PURE__ */ jsx("span", { className: props.className, title: "Native asset \xB7 no contract address", children: symbol });
  }
  return /* @__PURE__ */ jsx(AccountAddressDisplay, { ...props });
}
function AccountAddressDisplay({
  address,
  label: labelProp,
  fallbackLabel,
  entityLabel: entityLabelProp,
  chain,
  copyable = true,
  interactive = true,
  href,
  showExplorerIcon: showExplorerIconProp,
  className,
  monospace = true,
  internalHref: internalHrefProp,
  showInternalIcon = false,
  internalNewTab = false,
  onDrilldown: onDrilldownProp,
  onEditCatalog: onEditCatalogProp,
  tooltipRows: tooltipRowsProp,
  tooltipHeaderRight: tooltipHeaderRightProp,
  richTooltip,
  overlay: overlayProp,
  presentation: presentationProp,
  size: sizeProp,
  showAvatar: showAvatarProp,
  showRiskBadge: showRiskBadgeProp
}) {
  const defaults = React2.useContext(AddressDisplayContext);
  React2.useEffect(() => {
    if (overlayProp === void 0 && address) return defaults.registerAddress?.(address, chain);
  }, [defaults.registerAddress, address, chain, overlayProp]);
  const overlay = overlayProp === void 0 ? defaults.resolveOverlay?.(address, chain) : overlayProp;
  const internalHref = internalHrefProp ?? (href ? void 0 : defaults.getInternalHref?.(address, chain));
  const onDrilldown = onDrilldownProp ?? (href ? void 0 : defaults.onDrilldown);
  const onEditCatalog = onEditCatalogProp === void 0 ? defaults.onEditCatalog : onEditCatalogProp;
  const presentation = presentationProp ?? defaults.presentation ?? "plain";
  const size = sizeProp ?? defaults.size ?? "md";
  const showAvatar = showAvatarProp ?? defaults.showAvatar ?? presentation === "chip";
  const showRiskBadge = showRiskBadgeProp ?? defaults.showRiskBadge ?? presentation === "chip";
  const showExplorerIcon = showExplorerIconProp ?? defaults.showExplorerIcon ?? presentation === "chip";
  const label = labelProp ?? overlay?.label ?? overlay?.primaryEntityName ?? fallbackLabel;
  const entityLabel = entityLabelProp ?? overlay?.primaryEntityName ?? overlay?.label;
  const overlayRows = React2.useMemo(() => overlayTooltipRows(overlay), [overlay]);
  const tooltipRows = tooltipRowsProp && tooltipRowsProp.length ? [...tooltipRowsProp, ...overlayRows] : overlayRows;
  const tooltipHeaderRight = tooltipHeaderRightProp ?? (overlay?.riskLevel ? /* @__PURE__ */ jsx(RiskBadge, { level: overlay.riskLevel }) : void 0);
  const normalized = normalizeAddress(address, chain);
  const handleCopy = React2.useCallback(
    (e) => {
      e.preventDefault();
      e.clipboardData.setData("text/plain", normalized);
    },
    [normalized]
  );
  const link = href ?? (chain ? getExplorerUrl(normalized, chain, "address") : null) ?? void 0;
  const text = /* @__PURE__ */ jsx(MiddleEllipsis, { value: normalized, kind: "address", monospace, title: normalized, className: "min-w-0 text-[length:inherit]" });
  const handleInternalClick = React2.useCallback(
    (e) => {
      e.stopPropagation();
      if (!opensInApp(e)) return;
      if (!onDrilldown) return;
      e.preventDefault();
      onDrilldown(focusAddress(chain ?? "", address, entityLabel ?? label));
    },
    [onDrilldown, chain, address, entityLabel, label]
  );
  let value;
  if (!interactive) {
    value = text;
  } else if (internalHref) {
    value = /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 max-w-full items-center gap-1", children: [
      /* @__PURE__ */ jsxs(
        "a",
        {
          href: internalHref,
          target: internalNewTab ? "_blank" : void 0,
          rel: internalNewTab ? "noreferrer noopener" : void 0,
          onClick: internalNewTab ? (e) => e.stopPropagation() : handleInternalClick,
          className: "inline-flex min-w-0 max-w-full items-center gap-1 hover:underline",
          children: [
            text,
            showInternalIcon && /* @__PURE__ */ jsx(ArrowUpRight, { className: "h-3 w-3 shrink-0", "aria-hidden": true })
          ]
        }
      ),
      showExplorerIcon && link && /* @__PURE__ */ jsx(
        "a",
        {
          href: link,
          target: "_blank",
          rel: "noreferrer noopener",
          className: "shrink-0 text-muted-foreground hover:text-foreground",
          "aria-label": "Open in block explorer",
          onClick: (e) => e.stopPropagation(),
          children: /* @__PURE__ */ jsx(ExternalLink, { className: "h-3 w-3" })
        }
      )
    ] });
  } else if (onDrilldown) {
    value = /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: (e) => {
          e.stopPropagation();
          onDrilldown(focusAddress(chain ?? "", address, entityLabel ?? label));
        },
        className: "inline-flex min-w-0 max-w-full items-center gap-1 hover:underline",
        children: text
      }
    );
  } else if (link) {
    value = /* @__PURE__ */ jsxs("a", { href: link, onClick: (e) => e.stopPropagation(), className: "inline-flex min-w-0 max-w-full items-center gap-1 hover:underline", target: "_blank", rel: "noreferrer noopener", children: [
      text,
      showExplorerIcon && /* @__PURE__ */ jsx(ExternalLink, { className: "h-3 w-3" })
    ] });
  } else {
    value = text;
  }
  const enableTooltip = richTooltip ?? Boolean(
    entityLabel || onDrilldown || internalHref || tooltipRows && tooltipRows.length || tooltipHeaderRight
  );
  if (interactive && enableTooltip) {
    const actions = [];
    if (onDrilldown) {
      actions.push({
        icon: Search,
        label: "Investigate",
        onClick: () => onDrilldown(focusAddress(chain ?? "", address, entityLabel ?? label))
      });
    }
    if (link) {
      actions.push({ icon: ExternalLink, label: "Explorer", onClick: () => window.open(link, "_blank", "noopener,noreferrer") });
    }
    value = /* @__PURE__ */ jsx(
      RichTooltip,
      {
        title: entityLabel ?? label ?? "Address",
        subtitle: /* @__PURE__ */ jsx("span", { className: "font-mono", children: normalized }),
        headerRight: tooltipHeaderRight,
        rows: tooltipRows,
        actions,
        children: value
      }
    );
  }
  const content = /* @__PURE__ */ jsxs(Fragment, { children: [
    label && /* @__PURE__ */ jsx("span", { "data-address-label": true, className: cn("min-w-0 truncate font-medium leading-tight", presentation === "inline" && "max-w-48 flex-1"), title: label, children: interactive && internalHref ? /* @__PURE__ */ jsx(
      "a",
      {
        href: internalHref,
        target: internalNewTab ? "_blank" : void 0,
        rel: internalNewTab ? "noreferrer noopener" : void 0,
        onClick: internalNewTab ? (e) => e.stopPropagation() : handleInternalClick,
        className: "hover:underline",
        children: label
      }
    ) : label }),
    /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 max-w-full items-center gap-1.5 text-muted-foreground", onCopy: handleCopy, children: [
      value,
      interactive && copyable && /* @__PURE__ */ jsx(CopyButton, { value: normalized, className: "shrink-0" }),
      interactive && onEditCatalog && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          "aria-label": `Edit catalog for ${normalized}`,
          "aria-haspopup": "dialog",
          title: "Edit catalog",
          className: "inline-flex min-h-6 min-w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          onClick: (e) => {
            e.stopPropagation();
            e.currentTarget.focus();
            onEditCatalog(focusAddress(chain ?? "", address, entityLabel ?? label));
          },
          children: /* @__PURE__ */ jsx(Tag, { className: "h-3 w-3", "aria-hidden": true })
        }
      )
    ] })
  ] });
  return (
    // Bounded by its container at every level. Each span here was content-sized,
    // so in a narrow column the whole block overflowed the cell and was clipped by
    // it — the ellipsis inside never engaged, a long name was cut mid-word and the
    // copy button fell outside the cell.
    /* @__PURE__ */ jsxs("span", { "data-address-display": presentation, "data-address-size": size, className: cn(
      "inline-flex min-w-0 max-w-full items-center gap-1.5 align-middle",
      size === "sm" ? "text-xs" : size === "md" ? "text-sm" : "text-[length:inherit]",
      presentation === "chip" && "rounded-md border bg-muted/30",
      presentation === "chip" && (size === "sm" ? "px-1.5 py-0.5" : "px-2 py-1"),
      className
    ), children: [
      showAvatar && /* @__PURE__ */ jsx(AddressAvatar, { logoUrl: overlay?.logo, color: overlay?.color, name: label ?? normalized, size: "sm" }),
      /* @__PURE__ */ jsx("span", { className: cn("inline-flex min-w-0 max-w-full", presentation === "inline" ? "items-center gap-1.5" : "flex-col gap-0.5"), children: content }),
      showRiskBadge && overlay?.riskLevel && /* @__PURE__ */ jsx(RiskBadge, { level: overlay.riskLevel, className: "shrink-0" })
    ] })
  );
}
var STYLES2 = {
  [CatalogLifecycle.VERIFIED]: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  [CatalogLifecycle.CANDIDATE]: "text-muted-foreground border-border",
  [CatalogLifecycle.DEPRECATED]: "text-amber-700 dark:text-amber-400 border-amber-500/40",
  [CatalogLifecycle.QUARANTINED]: "bg-destructive/10 text-destructive border-destructive/30"
};
var LABELS2 = {
  [CatalogLifecycle.VERIFIED]: "Verified",
  [CatalogLifecycle.CANDIDATE]: "Candidate",
  [CatalogLifecycle.DEPRECATED]: "Deprecated",
  [CatalogLifecycle.QUARANTINED]: "Quarantined"
};
var DESCRIPTIONS = {
  [CatalogLifecycle.VERIFIED]: "Verified: a person or a trusted process confirmed this record.",
  [CatalogLifecycle.CANDIDATE]: "Candidate: this record was discovered automatically and nobody has curated it yet. It is used, but not confirmed.",
  [CatalogLifecycle.DEPRECATED]: "Deprecated: superseded, and de-prioritised in queries.",
  [CatalogLifecycle.QUARANTINED]: "Quarantined: suppressed as untrusted, pending review."
};
function lifecycleDescription(lifecycle) {
  return DESCRIPTIONS[lifecycle ?? CatalogLifecycle.CANDIDATE];
}
function LifecycleBadge({ lifecycle, className }) {
  const key = lifecycle ?? CatalogLifecycle.CANDIDATE;
  const style = STYLES2[key] ?? "text-muted-foreground border-border";
  const label = LABELS2[key] ?? String(lifecycle ?? "candidate");
  return /* @__PURE__ */ jsx(
    Badge,
    {
      variant: "outline",
      className: cn("border text-[11px] capitalize", style, className),
      title: DESCRIPTIONS[key],
      children: label
    }
  );
}
function ChipRemoveButton({
  label,
  onRemove,
  disabled,
  className
}) {
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        onRemove();
      },
      disabled,
      "aria-label": `Remove ${label}`,
      className: cn(
        "ml-0.5 opacity-70 transition-opacity hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-40",
        className
      ),
      children: "\xD7"
    }
  );
}
function SeverityWord({ severity }) {
  if (severity === Severity.INFO) return null;
  return /* @__PURE__ */ jsx("span", { className: "sr-only", children: ` (${severityLabel(severity)} severity)` });
}
var SEVERITY_STYLES = {
  [Severity.INFO]: "bg-muted text-muted-foreground border-muted",
  [Severity.LOW]: "bg-risk-low/15 text-risk-low border-risk-low/30",
  [Severity.MEDIUM]: "bg-risk-medium/15 text-risk-medium border-risk-medium/30",
  [Severity.HIGH]: "bg-risk-high/15 text-risk-high border-risk-high/30",
  [Severity.CRITICAL]: "bg-risk-critical/15 text-risk-critical border-risk-critical/30"
};
function TagGlyph({
  logo,
  logoCrossOrigin,
  color
}) {
  if (logo) {
    return /* @__PURE__ */ jsx(
      "img",
      {
        src: logo,
        ...logoCrossOrigin ? { crossOrigin: logoCrossOrigin } : {},
        alt: "",
        "aria-hidden": true,
        className: "-ml-0.5 h-3.5 w-3.5 shrink-0 rounded-full object-cover"
      }
    );
  }
  if (color) {
    return /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": true,
        className: "-ml-0.5 h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10",
        style: { backgroundColor: color }
      }
    );
  }
  return null;
}
function Tag2({
  label,
  severity = Severity.INFO,
  logo,
  logoCrossOrigin,
  color,
  onRemove,
  removeDisabled,
  className
}) {
  return /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: cn("border gap-1", SEVERITY_STYLES[severity], className), children: [
    /* @__PURE__ */ jsx(TagGlyph, { logo, logoCrossOrigin, color }),
    /* @__PURE__ */ jsx("span", { children: label }),
    /* @__PURE__ */ jsx(SeverityWord, { severity }),
    onRemove && /* @__PURE__ */ jsx(ChipRemoveButton, { label, onRemove, disabled: removeDisabled })
  ] });
}
var SCOPE_STYLES = {
  user: { ring: "border-brand/40", dot: "bg-brand", title: "Your override" },
  tenant: { ring: "border-finality-confirmed/40", dot: "bg-finality-confirmed", title: "Tenant override" },
  // Also previously unreachable: an org-wide overlay reported itself as one
  // tenant's, so a reader in a sibling tenant could not tell why they saw it.
  org: { ring: "border-finality-pending/40", dot: "bg-finality-pending", title: "Set for your organization" },
  provider: { ring: "border-risk-medium/40", dot: "bg-risk-medium", title: "Provider candidate" },
  entity: { ring: "border-finality-finalized/40", dot: "bg-finality-finalized", title: "Inherited from entity" },
  // Named for what it IS, because readers now actually see it. This tier was
  // unreachable — the backend classified every platform-shipped row as a
  // `tenant` override — so "Default" was a word nothing ever rendered. On an
  // account whose catalog is only the bundled set, it is now the commonest chip
  // on the screen, and "Default" alone reads as "unset" rather than "built in".
  default: { ring: "border-muted", dot: "bg-muted-foreground/50", title: "Platform default \u2014 built in, not editable" }
};
function CatalogLabel({
  label,
  value,
  scope,
  showScope,
  severity,
  logo,
  logoCrossOrigin,
  color,
  onRemove,
  removeDisabled,
  className
}) {
  const style = scope ? SCOPE_STYLES[scope] : null;
  const withScope = showScope ?? Boolean(scope);
  const weight = severity && severity !== Severity.INFO ? SEVERITY_STYLES[severity] : void 0;
  return /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: cn("gap-1.5 border", weight ?? style?.ring, className), children: [
    logo ? /* @__PURE__ */ jsx(
      "img",
      {
        src: logo,
        ...logoCrossOrigin ? { crossOrigin: logoCrossOrigin } : {},
        alt: "",
        "aria-hidden": true,
        className: "-ml-0.5 h-3.5 w-3.5 shrink-0 rounded-full object-cover"
      }
    ) : color ? /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": true,
        className: "-ml-0.5 h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10",
        style: { backgroundColor: color }
      }
    ) : null,
    withScope && style && // The dot's whole meaning was a `title` on an `aria-hidden` element —
    // a tooltip nothing could reach and a name nothing could read, so
    // provenance ("Your override", "Platform default") existed for the
    // mouse alone. `role="img"` + `aria-label` is the pattern
    // `CriticalityBadge` already uses for exactly this: a glyph that is
    // decoration to look at and a sentence to listen to. The `title` stays
    // for the pointer, now on an element that is actually exposed.
    /* @__PURE__ */ jsx(
      "span",
      {
        role: "img",
        "aria-label": style.title,
        title: style.title,
        className: cn("h-1.5 w-1.5 rounded-full", style.dot)
      }
    ),
    /* @__PURE__ */ jsx("span", { className: "font-medium", children: label }),
    value != null && /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: value }),
    severity && /* @__PURE__ */ jsx(SeverityWord, { severity }),
    onRemove && /* @__PURE__ */ jsx(ChipRemoveButton, { label, onRemove, disabled: removeDisabled })
  ] });
}
var ACCENTS = [
  "text-sky-600 bg-sky-500/10 border-sky-500/30",
  "text-violet-600 bg-violet-500/10 border-violet-500/30",
  "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
  "text-amber-600 bg-amber-500/10 border-amber-500/30",
  "text-rose-600 bg-rose-500/10 border-rose-500/30",
  "text-cyan-600 bg-cyan-500/10 border-cyan-500/30",
  "text-fuchsia-600 bg-fuchsia-500/10 border-fuchsia-500/30"
];
function accentFor(id) {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = hash * 31 + id.charCodeAt(i) >>> 0;
  return ACCENTS[hash % ACCENTS.length];
}
function GroupBadge({
  groupId,
  name,
  count,
  icon,
  onClick,
  onRemove,
  removeDisabled,
  className
}) {
  const accent = accentFor(groupId);
  const label = name ?? groupId;
  const body = /* @__PURE__ */ jsxs(Fragment, { children: [
    icon ?? /* @__PURE__ */ jsx(Boxes, { className: "h-3 w-3" }),
    /* @__PURE__ */ jsx("span", { children: label }),
    count != null && /* @__PURE__ */ jsxs("span", { className: "opacity-70", children: [
      "\xB7 ",
      count
    ] })
  ] });
  const shell = cn(
    "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
    accent,
    className
  );
  if (onClick) {
    return /* @__PURE__ */ jsxs("span", { className: cn(shell, "pr-1"), title: label, children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick,
          className: "inline-flex items-center gap-1.5 transition-opacity hover:opacity-80",
          children: body
        }
      ),
      onRemove && /* @__PURE__ */ jsx(ChipRemoveButton, { label, onRemove, disabled: removeDisabled })
    ] });
  }
  return /* @__PURE__ */ jsxs("span", { className: cn(shell, onRemove && "pr-1"), title: label, children: [
    body,
    onRemove && /* @__PURE__ */ jsx(ChipRemoveButton, { label, onRemove, disabled: removeDisabled })
  ] });
}
function splitOverflow(total, max) {
  const safeMax = Math.max(0, max);
  if (total <= safeMax) return { visible: total, overflow: 0 };
  const visible = Math.max(0, safeMax - 1);
  return { visible, overflow: total - visible };
}
function TagList({
  items,
  max = 3,
  popoverTitle,
  className,
  align = "start"
}) {
  if (items.length === 0) return null;
  const { visible, overflow } = splitOverflow(items.length, max);
  return /* @__PURE__ */ jsxs("span", { className: cn("inline-flex flex-wrap items-center gap-1", className), children: [
    items.slice(0, visible).map((item, i) => /* @__PURE__ */ jsx(React2.Fragment, { children: item }, i)),
    overflow > 0 && /* @__PURE__ */ jsxs(Popover, { children: [
      /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: (e) => e.stopPropagation(),
          className: "inline-flex",
          "aria-label": `Show ${overflow} more`,
          children: /* @__PURE__ */ jsxs(
            Badge,
            {
              variant: "outline",
              className: "cursor-pointer text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              children: [
                "+",
                overflow
              ]
            }
          )
        }
      ) }),
      /* @__PURE__ */ jsxs(PopoverContent, { align, className: "w-auto max-w-xs p-2", children: [
        popoverTitle && /* @__PURE__ */ jsx("div", { className: "mb-2 px-1 text-xs font-medium text-muted-foreground", children: popoverTitle }),
        /* @__PURE__ */ jsx("div", { className: "flex max-h-64 flex-wrap gap-1 overflow-y-auto", children: items.map((item, i) => /* @__PURE__ */ jsx(React2.Fragment, { children: item }, i)) })
      ] })
    ] })
  ] });
}
var CutoffsContext = React2.createContext(null);
function ConfidenceCutoffsProvider({
  cutoffs,
  children
}) {
  const value = React2.useMemo(
    () => cutoffs && (cutoffs.high != null || cutoffs.medium != null) ? { high: cutoffs.high, medium: cutoffs.medium } : null,
    [cutoffs?.high, cutoffs?.medium]
  );
  return /* @__PURE__ */ jsx(CutoffsContext.Provider, { value, children });
}
function useTierCutoffs() {
  const scoped = React2.useContext(CutoffsContext);
  return {
    high: scoped?.high ?? DEFAULT_TIER_CUTOFFS.high,
    medium: scoped?.medium ?? DEFAULT_TIER_CUTOFFS.medium
  };
}
function useTierFor() {
  const cutoffs = useTierCutoffs();
  return React2.useCallback(
    (score) => tierFor(score, cutoffs),
    [cutoffs.high, cutoffs.medium]
  );
}
function useCutoffsAreScoped() {
  const scoped = React2.useContext(CutoffsContext);
  return scoped != null;
}
var KIND_PHRASE = {
  trace_opcode: "stated by the trace opcode",
  call_shape: "classified from the call's shape",
  registry: "pinned by the protocol registry",
  abi: "decoded from a verified ABI",
  proxy_abi: "decoded from the proxy implementation ABI",
  wire_layout: "decoded from a fixed wire layout",
  provided: "supplied by an upstream producer",
  generic: "built by the generic fallback",
  heuristic: "inferred heuristically",
  provider: "asserted by provider",
  user: "asserted by a human",
  automation: "asserted by a trusted process",
  /**
   * The EXTERNAL-audience phrase, and the one most likely to be rendered to a
   * customer: `EvidenceSource.redacted()` collapses every INTERNAL (third-party)
   * source to `SourceKind.AGGREGATE` precisely so the provider we scraped is
   * never named. It was missing here, and the lookup below falls back to the raw
   * kind — so the redaction path, the whole reason the visibility split exists,
   * rendered the bare token "aggregate" to exactly the audience it protects.
   */
  aggregate: "aggregated from multiple sources",
  unknown: "of unknown origin"
};
var TIER_BAR = {
  [ConfidenceTier.HIGH]: "bg-emerald-500",
  [ConfidenceTier.MEDIUM]: "bg-amber-500",
  [ConfidenceTier.LOW]: "bg-destructive"
};
function explainEvidence(ev) {
  const source = ev.source ?? {};
  const phrase = source.kind ? KIND_PHRASE[source.kind] ?? source.kind : "of unknown origin";
  const who = source.id ? `${phrase} ${source.id}` : phrase;
  const score = coerceConfidence(ev.confidence);
  const bullets = [
    score == null ? who : `${who} (confidence ${score.toFixed(2)})`
  ];
  for (const reason of ev.reasons ?? []) bullets.push(reason);
  const active = Object.entries(ev.signals ?? {}).filter(([, v]) => Boolean(v)).map(([k]) => k).sort();
  if (active.length > 0) bullets.push(`signals: ${active.join(", ")}`);
  if (source.url) bullets.push(`source: ${source.url}`);
  return bullets;
}
function byConfidence(evidence) {
  return [...evidence].sort(
    (a, b) => (coerceConfidence(b.confidence) ?? 0) - (coerceConfidence(a.confidence) ?? 0)
  );
}
function WhyPanel({
  explanation,
  evidence,
  confidence,
  cutoffs = DEFAULT_TIER_CUTOFFS,
  confidenceNote,
  lifecycle,
  record,
  title = "Why?",
  className
}) {
  const evidenceList = React2.useMemo(() => {
    if (!evidence) return [];
    return Array.isArray(evidence) ? evidence : [evidence];
  }, [evidence]);
  const bullets = React2.useMemo(() => {
    const persisted = (explanation ?? []).filter(Boolean);
    if (persisted.length > 0) return persisted;
    return byConfidence(evidenceList).flatMap((ev) => explainEvidence(ev).map((b) => `- ${b}`));
  }, [explanation, evidenceList]);
  const score = coerceConfidence(confidence);
  const note = score == null && confidenceNote?.trim() ? confidenceNote.trim() : null;
  const recorded = (record ?? []).filter((line) => line?.trim());
  if (bullets.length === 0 && score == null && note == null && !lifecycle && recorded.length === 0) {
    return null;
  }
  const pct = score == null ? null : clampConfidence(score);
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-2 text-xs", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: title }),
      lifecycle && /* @__PURE__ */ jsx(LifecycleBadge, { lifecycle })
    ] }),
    pct != null && /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between text-[11px] text-muted-foreground", children: [
        /* @__PURE__ */ jsx("span", { children: "Confidence" }),
        /* @__PURE__ */ jsx("span", { className: "tabular-nums", children: formatConfidence(pct) })
      ] }),
      /* @__PURE__ */ jsx(
        "div",
        {
          className: "h-1.5 w-full overflow-hidden rounded-full bg-muted",
          role: "progressbar",
          "aria-valuenow": Math.round(pct * 100),
          "aria-valuemin": 0,
          "aria-valuemax": 100,
          children: /* @__PURE__ */ jsx(
            "div",
            {
              className: cn("h-full rounded-full", TIER_BAR[tierFor(pct, cutoffs)]),
              style: { width: `${Math.round(pct * 100)}%` }
            }
          )
        }
      )
    ] }),
    note != null && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2 text-[11px] text-muted-foreground", children: [
      /* @__PURE__ */ jsx("span", { children: "Confidence" }),
      /* @__PURE__ */ jsx("span", { className: "text-right", children: note })
    ] }),
    bullets.length > 0 ? /* @__PURE__ */ jsx("ul", { className: "space-y-1 text-muted-foreground", children: bullets.map((bullet, i) => /* @__PURE__ */ jsx("li", { className: "leading-snug", children: bullet.replace(/^-\s*/, "\u2022 ") }, i)) }) : (
      // Says what is missing, not that nothing is known: the row's own record
      // is rendered below and is often the only history there is.
      /* @__PURE__ */ jsx("p", { className: "text-muted-foreground", children: "No supporting evidence or explanation was recorded." })
    ),
    recorded.length > 0 && /* @__PURE__ */ jsx("ul", { className: "space-y-1 border-t pt-2 text-muted-foreground", children: recorded.map((line, i) => /* @__PURE__ */ jsx("li", { className: "leading-snug", children: line }, i)) })
  ] });
}
function WhyPopover({
  children,
  ...panel
}) {
  const content = WhyPanel(panel);
  if (content == null) return null;
  if (children == null) return /* @__PURE__ */ jsxs(Popover, { children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx("button", { type: "button", className: cn(badgeVariants({ variant: "outline" }), "cursor-pointer border-dashed text-[10px] text-muted-foreground"), children: "Why?" }) }),
    /* @__PURE__ */ jsx(PopoverContent, { align: "start", "aria-label": "Why this confidence assessment?", children: content })
  ] });
  return /* @__PURE__ */ jsxs(HoverCard, { children: [
    /* @__PURE__ */ jsx(HoverCardTrigger, { asChild: true, children }),
    /* @__PURE__ */ jsx(HoverCardContent, { align: "start", className: "w-72", children: content })
  ] });
}
var Context = React2.createContext({ policy: readConfidencePolicy(), loading: false });
function ReadConfidenceProvider({ policy, loading = false, children }) {
  const value = React2.useMemo(() => ({ policy, loading }), [policy, loading]);
  return /* @__PURE__ */ jsx(Context.Provider, { value, children });
}
function useReadConfidence() {
  const { policy, loading } = React2.useContext(Context);
  return { policy, loading, accepts: (score) => !loading && meetsReadConfidence(policy, score) };
}
function ReadConfidenceBadge({ score, evidence, explanation, lifecycle, record, confidenceNote }) {
  const cutoffs = useTierCutoffs();
  const valid = typeof score === "number" && Number.isFinite(score) && score >= 0 && score <= 1;
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1 text-xs", children: [
    /* @__PURE__ */ jsx(Badge, { variant: "outline", children: valid ? `Confidence ${Number((score * 100).toFixed(2))}%` : "Not scored" }),
    /* @__PURE__ */ jsx(
      WhyPopover,
      {
        confidence: valid ? score : null,
        confidenceNote: valid ? null : confidenceNote ?? "No confidence was recorded for this interpretation.",
        evidence,
        explanation,
        lifecycle,
        record,
        cutoffs
      }
    )
  ] });
}
function readConfidenceHiddenText({
  subject,
  score,
  policy,
  loading
}) {
  if (loading) return "Loading read confidence preference\u2026";
  const reason = score == null ? "unscored findings are excluded" : `floor ${Number((policy.floor * 100).toFixed(2))}%`;
  return `${subject} hidden by your read confidence preference (${reason}). Change it in Settings \u2192 Defaults to review this interpretation.`;
}
function ReadConfidenceBoundary({ children, rawFallback, subject = "Interpretation", ...assessment }) {
  const { accepts, policy, loading } = useReadConfidence();
  const [revealed, setRevealed] = React2.useState(false);
  const passes = accepts(assessment.score);
  const visible = passes || revealed;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", "data-confidence-visible": visible, "data-confidence-passes": passes, children: [
    /* @__PURE__ */ jsx(ReadConfidenceBadge, { ...assessment }),
    passes ? children : /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", role: "status", children: readConfidenceHiddenText({ subject, score: assessment.score, policy, loading }) }),
      loading ? null : /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => setRevealed(!revealed),
          className: "text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground",
          children: revealed ? "Hide it again" : "Show it anyway"
        }
      ),
      revealed ? children : null,
      rawFallback
    ] })
  ] });
}
function primaryFirst(items) {
  return [...items].sort((a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary)));
}
function ChipSlot({
  kind,
  item,
  prefs,
  renderChip,
  renderEntityLink
}) {
  const img = useSignedCdnImage(prefs.logos ? item.logo : void 0);
  const tierCutoffs = useTierCutoffs();
  const glyph = prefs.logos ? { logo: img.src, logoCrossOrigin: img.crossOrigin, color: item.color } : {};
  const chip = explained(chipFor(kind, item, prefs, glyph, renderEntityLink), item, prefs, tierCutoffs);
  return /* @__PURE__ */ jsx(Fragment, { children: renderChip ? renderChip(chip, item, kind) : chip });
}
function explained(chip, item, prefs, cutoffs) {
  const explanation = item.explanation?.filter(Boolean) ?? [];
  const confidence = scoredConfidence(item.confidence, item);
  const scored = confidence !== void 0;
  if (!prefs.showWhy || explanation.length === 0 && !scored && !item.lifecycle) return chip;
  return /* @__PURE__ */ jsx(
    WhyPopover,
    {
      confidence,
      cutoffs,
      explanation,
      lifecycle: item.lifecycle,
      title: item.name,
      children: /* @__PURE__ */ jsx("span", { className: "inline-flex cursor-help", children: chip })
    }
  );
}
function chipFor(kind, item, prefs, glyph, renderEntityLink) {
  switch (kind) {
    case "label":
      return /* @__PURE__ */ jsx(
        CatalogLabel,
        {
          label: item.name,
          value: item.value,
          scope: item.scope,
          showScope: prefs.showProvenance && Boolean(item.scope),
          severity: item.severity,
          onRemove: item.onRemove,
          removeDisabled: item.removeDisabled,
          ...glyph
        }
      );
    case "tag":
      return /* @__PURE__ */ jsx(
        Tag2,
        {
          label: item.name,
          severity: item.severity,
          onRemove: item.onRemove,
          removeDisabled: item.removeDisabled,
          ...glyph
        }
      );
    case "group":
      return /* @__PURE__ */ jsx(
        GroupBadge,
        {
          groupId: item.id,
          name: item.name,
          count: item.count,
          onRemove: item.onRemove,
          removeDisabled: item.removeDisabled
        }
      );
    case "entity":
      return /* @__PURE__ */ jsxs(Badge, { variant: "secondary", className: "gap-1.5 font-normal", title: item.entityType ?? item.name, children: [
        item.isPrimary && // The headline entity for this address, not merely one it carries.
        // Sorting it first is not enough on a row of three — the star is
        // what says which one the reconciler will actually use.
        /* @__PURE__ */ jsx(Star, { "aria-label": "Primary", className: "-ml-0.5 h-3 w-3 shrink-0 fill-current text-amber-500" }),
        glyph.logo ? /* @__PURE__ */ jsx(
          "img",
          {
            src: glyph.logo,
            ...glyph.logoCrossOrigin ? { crossOrigin: glyph.logoCrossOrigin } : {},
            alt: "",
            "aria-hidden": true,
            className: "-ml-0.5 h-3.5 w-3.5 shrink-0 rounded-full object-cover"
          }
        ) : glyph.color ? /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": true,
            className: "-ml-0.5 h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10",
            style: { backgroundColor: glyph.color }
          }
        ) : null,
        /* @__PURE__ */ jsx("span", { children: renderEntityLink ? renderEntityLink(item, item.name) : item.name }),
        item.onRemove && /* @__PURE__ */ jsx(
          ChipRemoveButton,
          {
            label: item.name,
            onRemove: item.onRemove,
            disabled: item.removeDisabled
          }
        )
      ] });
  }
}
function AttributionChips({
  bundle,
  withheld = 0,
  floorMode = "hide",
  userPrefs,
  popoverTitle = "Attribution",
  renderChip,
  renderEntityLink,
  empty = null,
  className,
  ...override
}) {
  const prefs = resolveAttributionChipPrefs(override, userPrefs);
  const { policy, loading } = useReadConfidence();
  const withheldByServer = Math.max(0, Math.trunc(withheld) || 0);
  const { items, hidden } = React2.useMemo(() => {
    const out = [];
    let hidden2 = 0;
    for (const kind of prefs.kinds) {
      for (const item of primaryFirst(bundle[BUNDLE_KEY[kind]] ?? [])) {
        const score = scoredConfidence(item.confidence, item);
        const below = !loading && policy.floor > 0 && typeof score === "number" && score < policy.floor;
        if (below && floorMode === "hide") {
          hidden2 += 1;
          continue;
        }
        const slot = /* @__PURE__ */ jsx(
          ChipSlot,
          {
            kind,
            item,
            prefs,
            renderChip,
            renderEntityLink
          },
          `${kind}:${item.id}`
        );
        out.push(
          below ? /* @__PURE__ */ jsx(
            "span",
            {
              className: "inline-flex opacity-60",
              "data-below-floor": "true",
              title: `Below your ${Number((policy.floor * 100).toFixed(2))}% confidence floor \u2014 this is not shown on reading surfaces.`,
              children: slot
            },
            `${kind}:${item.id}`
          ) : slot
        );
      }
    }
    return { items: out, hidden: hidden2 + withheldByServer };
  }, [bundle, floorMode, loading, policy.floor, prefs, renderChip, renderEntityLink, withheldByServer]);
  const hiddenNote = hidden > 0 ? /* @__PURE__ */ jsxs(
    Badge,
    {
      variant: "outline",
      className: "border-dashed font-normal text-muted-foreground",
      title: `${hidden} attribution${hidden === 1 ? "" : "s"} scored below the minimum confidence your policy accepts (${Number(
        (policy.floor * 100).toFixed(2)
      )}%). Change it in Settings \u2192 Defaults.`,
      children: [
        hidden,
        " below your floor"
      ]
    },
    "__below_floor__"
  ) : null;
  if (items.length === 0) return hiddenNote ? /* @__PURE__ */ jsx("span", { className: "inline-flex", children: hiddenNote }) : /* @__PURE__ */ jsx(Fragment, { children: empty });
  return /* @__PURE__ */ jsxs("span", { className: "inline-flex flex-wrap items-center gap-1", children: [
    /* @__PURE__ */ jsx(
      TagList,
      {
        items,
        max: prefs.max,
        popoverTitle,
        className: cn(prefs.size === "sm" && "text-xs", className)
      }
    ),
    hiddenNote
  ] });
}

export { AddressAvatar, AddressDisplay, AddressDisplayProvider, AttributionChips, CatalogLabel, ChainAvatar, ChipRemoveButton, ConfidenceCutoffsProvider, GroupBadge, KIND_PHRASE, LifecycleBadge, ReadConfidenceBadge, ReadConfidenceBoundary, ReadConfidenceProvider, RiskBadge, SEVERITY_STYLES, SeverityWord, Tag2 as Tag, TagList, WhyPanel, WhyPopover, explainEvidence, lifecycleDescription, readConfidenceHiddenText, splitOverflow, useCutoffsAreScoped, useReadConfidence, useSignedCdnImage, useTierCutoffs, useTierFor };
//# sourceMappingURL=chunk-Y6AJEV52.js.map
//# sourceMappingURL=chunk-Y6AJEV52.js.map